export enum RoutePath {
  Dashboard = "/",
  Login = "/",
  VerifyEmail = "/verify-email",
  Booking = "/booking",
  BookingPayment = "/booking/payment",
  ManageBookings = "/bookings",
  BookingDetail = "/bookings/:orderId",
}

export const urls = {
  privacy: "https://health4travel.com/privacy-policy/",
  termsOfUse: "https://health4travel.com/terms-and-condition/",
  returnsAndExchanges: "https://health4travel.com/privacy-policy/",
  contactUs: process.env.REACT_APP_CONTACT_URL ?? "",
  sendFeedback: process.env.REACT_APP_CONTACT_URL ?? "",
  facebook: "https://www.facebook.com/health4travel",
  twitter: "https://x.com/AirportVax",
  instagram: "https://www.instagram.com/health4travel",
  linkedin: "https://www.linkedin.com/company/health4travel/",
  bookingSuccess: ({
    order_id,
    code,
    telehealth_order = false,
    free = false,
  }: {
    order_id: string | number;
    code: string;
    telehealth_order?: boolean;
    free?: boolean;
  }) =>
    `/?paymentSuccess=true&order_id=${order_id}&code=${code}&telehealth_order=${telehealth_order}&free=${free}`,
} as const;

export type Country = {
  dial: `+${string}`;
  iso2: string;
  name: string;
  nationalFormat?: { re: RegExp; fmt: string };
};

/** Matches booking form defaults when no dial is present yet. */
export const defaultPhoneDial = "+31" as const;

export function flagSrc(iso2: string) {
  return `https://flagcdn.com/${iso2.toLowerCase()}.svg`;
}

export const countries: ReadonlyArray<Country> = [
  { dial: "+852", iso2: "hk", name: "Hong Kong" },
  { dial: "+880", iso2: "bd", name: "Bangladesh" },
  { dial: "+886", iso2: "tw", name: "Taiwan" },
  { dial: "+966", iso2: "sa", name: "Saudi Arabia" },
  { dial: "+968", iso2: "om", name: "Oman" },
  { dial: "+970", iso2: "ps", name: "Palestine" },
  {
    dial: "+971",
    iso2: "ae",
    name: "United Arab Emirates",
  },
  { dial: "+973", iso2: "bh", name: "Bahrain" },
  { dial: "+974", iso2: "qa", name: "Qatar" },
  { dial: "+965", iso2: "kw", name: "Kuwait" },
  { dial: "+233", iso2: "gh", name: "Ghana" },
  { dial: "+234", iso2: "ng", name: "Nigeria" },
  { dial: "+254", iso2: "ke", name: "Kenya" },
  { dial: "+255", iso2: "tz", name: "Tanzania" },
  { dial: "+256", iso2: "ug", name: "Uganda" },
  { dial: "+351", iso2: "pt", name: "Portugal" },
  { dial: "+352", iso2: "lu", name: "Luxembourg" },
  { dial: "+353", iso2: "ie", name: "Ireland" },
  { dial: "+354", iso2: "is", name: "Iceland" },
  { dial: "+356", iso2: "mt", name: "Malta" },
  { dial: "+357", iso2: "cy", name: "Cyprus" },
  {
    dial: "+358",
    iso2: "fi",
    name: "Finland",
    nationalFormat: { re: /^(\d{2})(\d{3})(\d{4})$/, fmt: "$1$2$3" },
  },
  { dial: "+359", iso2: "bg", name: "Bulgaria" },
  { dial: "+370", iso2: "lt", name: "Lithuania" },
  { dial: "+371", iso2: "lv", name: "Latvia" },
  { dial: "+372", iso2: "ee", name: "Estonia" },
  { dial: "+380", iso2: "ua", name: "Ukraine" },
  { dial: "+381", iso2: "rs", name: "Serbia" },
  { dial: "+382", iso2: "me", name: "Montenegro" },
  { dial: "+385", iso2: "hr", name: "Croatia" },
  { dial: "+386", iso2: "si", name: "Slovenia" },
  {
    dial: "+387",
    iso2: "ba",
    name: "Bosnia and Herzegovina",
  },
  { dial: "+389", iso2: "mk", name: "North Macedonia" },
  {
    dial: "+420",
    iso2: "cz",
    name: "Czechia",
    nationalFormat: { re: /^(\d{3})(\d{3})(\d{3})$/, fmt: "$1$2$3" },
  },
  {
    dial: "+421",
    iso2: "sk",
    name: "Slovakia",
    nationalFormat: { re: /^(\d{3})(\d{3})(\d{3})$/, fmt: "$1$2$3" },
  },
  { dial: "+20", iso2: "eg", name: "Egypt" },
  { dial: "+27", iso2: "za", name: "South Africa" },
  { dial: "+30", iso2: "gr", name: "Greece" },
  {
    dial: "+31",
    iso2: "nl",
    name: "Netherlands",
    nationalFormat: { re: /^(\d{3})(\d{3})(\d{3})$/, fmt: "$1$2$3" },
  },
  { dial: "+32", iso2: "be", name: "Belgium" },
  {
    dial: "+33",
    iso2: "fr",
    name: "France",
    nationalFormat: { re: /^(\d{3})(\d{3})(\d{3})$/, fmt: "$1$2$3" },
  },
  {
    dial: "+34",
    iso2: "es",
    name: "Spain",
    nationalFormat: { re: /^(\d{3})(\d{3})(\d{3})$/, fmt: "$1$2$3" },
  },
  { dial: "+36", iso2: "hu", name: "Hungary" },
  { dial: "+39", iso2: "it", name: "Italy" },
  { dial: "+40", iso2: "ro", name: "Romania" },
  { dial: "+41", iso2: "ch", name: "Switzerland" },
  { dial: "+43", iso2: "at", name: "Austria" },
  { dial: "+44", iso2: "gb", name: "United Kingdom" },
  { dial: "+45", iso2: "dk", name: "Denmark" },
  { dial: "+46", iso2: "se", name: "Sweden" },
  { dial: "+47", iso2: "no", name: "Norway" },
  { dial: "+48", iso2: "pl", name: "Poland" },
  { dial: "+49", iso2: "de", name: "Germany" },
  { dial: "+51", iso2: "pe", name: "Peru" },
  { dial: "+52", iso2: "mx", name: "Mexico" },
  { dial: "+54", iso2: "ar", name: "Argentina" },
  { dial: "+55", iso2: "br", name: "Brazil" },
  { dial: "+56", iso2: "cl", name: "Chile" },
  { dial: "+57", iso2: "co", name: "Colombia" },
  { dial: "+60", iso2: "my", name: "Malaysia" },
  { dial: "+61", iso2: "au", name: "Australia" },
  { dial: "+62", iso2: "id", name: "Indonesia" },
  { dial: "+63", iso2: "ph", name: "Philippines" },
  { dial: "+64", iso2: "nz", name: "New Zealand" },
  { dial: "+65", iso2: "sg", name: "Singapore" },
  { dial: "+66", iso2: "th", name: "Thailand" },
  { dial: "+81", iso2: "jp", name: "Japan" },
  { dial: "+82", iso2: "kr", name: "South Korea" },
  { dial: "+84", iso2: "vn", name: "Vietnam" },
  { dial: "+86", iso2: "cn", name: "China" },
  { dial: "+90", iso2: "tr", name: "Turkey" },
  { dial: "+91", iso2: "in", name: "India" },
  { dial: "+92", iso2: "pk", name: "Pakistan" },
  { dial: "+93", iso2: "af", name: "Afghanistan" },
  { dial: "+94", iso2: "lk", name: "Sri Lanka" },
  { dial: "+7", iso2: "ru", name: "Russia" },
  {
    dial: "+1",
    iso2: "us",
    name: "United States",
    nationalFormat: { re: /^(\d{3})(\d{3})(\d{4})$/, fmt: "$1$2$3" },
  },
] as const;
